import { useEffect } from "react";
import Lenis from "lenis";
import { motion, useScroll, useSpring } from "framer-motion";
import { useLocation } from "react-router-dom";

import { Nav } from "@/components/portfolio/Nav";
import { Hero } from "@/components/portfolio/Hero";
import { EditorialMarquee } from "@/components/portfolio/Marquee";
import { About } from "@/components/portfolio/About";
import { Experience } from "@/components/portfolio/Experience";
import { Research } from "@/components/portfolio/Research";
import { Education } from "@/components/portfolio/Education";
import { Leadership } from "@/components/portfolio/Leadership";
import { Skills } from "@/components/portfolio/Skills";
import { Writing } from "@/components/portfolio/Writing";
import { Faq } from "@/components/portfolio/Faq";
import { Contact } from "@/components/portfolio/Contact";
import { Footer } from "@/components/portfolio/Footer";
import { BackToTop } from "@/components/portfolio/BackToTop";

const Block = ({ theme, children }) => (
  <div className={`${theme} relative z-10`}>{children}</div>
);

export default function Portfolio() {
  const location = useLocation();
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });

  useEffect(() => {
    const target = location.state?.scrollTo;
    if (target) {
      setTimeout(() => document.getElementById(target)?.scrollIntoView({ behavior: "smooth" }), 250);
    }
  }, [location.state]);

  useEffect(() => {
    const hash = window.__initialHash || "";
    if (hash) {
      window.__initialHash = "";
      const el = document.getElementById(hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 350);
    }
    // Runs once on mount to honour an initial deep-link hash. Intentional [].
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      if (window.__lenis === lenis) window.__lenis = null;
    };
    // Initialise Lenis + rAF loop exactly once. Adding deps would re-create the
    // smooth-scroll instance on every render and cause visible flicker. Do not add deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative bg-[#eef0e9] min-h-screen">
      <div className="grain" />
      <motion.div
        style={{ scaleX }}
        className="fixed top-0 left-0 right-0 h-[2px] bg-[#b5423a] origin-left z-[60]"
      />
      <Nav />
      <main>
        <Hero />
        <Block theme="theme-black theme-dark"><EditorialMarquee /></Block>
        <Block theme="theme-white"><About /></Block>
        <Block theme="theme-purple theme-dark"><Experience /></Block>
        <Block theme="theme-pink"><Research /></Block>
        <Block theme="theme-white"><Education /></Block>
        <Block theme="theme-purple theme-dark"><Leadership /></Block>
        <Block theme="theme-black theme-dark"><Skills /></Block>
        <Block theme="theme-white"><Writing /></Block>
        <Block theme="theme-slate theme-dark"><Faq /></Block>
        <Block theme="theme-pink"><Contact /></Block>
        <Block theme="theme-black theme-dark"><Footer /></Block>
      </main>
      <BackToTop />
    </div>
  );
}
